import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

export type IndexRecordScheduleTimelinesResult =
  Diagonal.Workflows.Data.IndexRecordScheduleTimelinesData;

/** TanStack's `refetchInterval`, narrowed to this query's cached shape. */
type TimelinesRefetchInterval =
  | number
  | false
  | ((query: {
      state: { data: IndexRecordScheduleTimelinesResult | undefined };
    }) => number | false);

export function useRecordScheduleTimelines(
  resourceId: string | null,
  recordId: string | null,
  recordScheduleWorkflowId: string | null = null,
  options: { refetchInterval?: TimelinesRefetchInterval } = {}
) {
  return useQuery<IndexRecordScheduleTimelinesResult>({
    queryKey: ['record-schedule-timelines', resourceId, recordId, recordScheduleWorkflowId],
    queryFn: () =>
      axios
        .get(`/api/data/resources/${resourceId}/records/${recordId}/record-schedules`, {
          params: recordScheduleWorkflowId ? { workflow_id: recordScheduleWorkflowId } : {}
        })
        .then((res) => res.data),
    enabled: Boolean(resourceId && recordId),
    ...options
  });
}

export function useSkipRecordScheduleOffset(resourceId: string, recordId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workflowId,
      offset,
      scheduledRunAt
    }: {
      workflowId: string;
      offset: string;
      scheduledRunAt?: string;
    }): Promise<IndexRecordScheduleTimelinesResult> => {
      if (!recordId) {
        throw new Error('A record is required to skip a reminder.');
      }

      const response = await axios.post<IndexRecordScheduleTimelinesResult>(
        `/api/data/resources/${resourceId}/records/${recordId}/record-schedules/${workflowId}/skip`,
        { offset, scheduled_run_at: scheduledRunAt }
      );
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['record-schedule-timelines', resourceId, recordId]
      });
    }
  });
}
